// US16 U5 — pure team-grouping for the Tasks index. Stateless: same
// input → same output, recomputed from scratch on every Inertia reload.
//
// Rule (plan §U5):
//
//   * Buckets `TaskRow[]` by `team_id` and orders the buckets by the
//     server-provided `teamGroups` sequence. The server already emits
//     `team_groups` in ancestry-hierarchy order and only for non-empty
//     teams (US16 U2), so this function never re-sorts and never invents
//     empty groups — it iterates `teamGroups` and attaches each team's
//     tasks.
//   * Each group carries the server's TRUE per-team `count`, which can
//     exceed `tasks.length` because the grouped payload caps rendered
//     rows per team (PER_TEAM_LIST_CAP / Kanban per-status caps). The
//     view uses `count - tasks.length` for the "voir N de plus"
//     disclosure.
//   * Tasks whose `team_id` matches no group are dropped. Given U2 this
//     shouldn't happen (every present team has a group), but the guard
//     keeps the render defensive against a payload skew.
//
// Mirrors the pure-function discipline of `kanban-grouping.ts`.

import type { TaskRow, TeamGroup } from '@/types/work'

export interface TeamSection {
  group: TeamGroup
  tasks: TaskRow[]
}

export function groupTasksByTeam(
  tasks: TaskRow[],
  teamGroups: TeamGroup[],
): TeamSection[] {
  const tasksByTeamId = new Map<number, TaskRow[]>()
  for (const t of tasks) {
    const list = tasksByTeamId.get(t.team_id) ?? []
    list.push(t)
    tasksByTeamId.set(t.team_id, list)
  }

  // Iterate teamGroups (hierarchy-ordered, non-empty) so the rendered
  // order matches the server exactly; tasks with no matching group fall
  // out by never being read from the map.
  return teamGroups.map((group) => ({
    group,
    tasks: tasksByTeamId.get(group.id) ?? [],
  }))
}
